import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ActivateAlertRuleRequest,
  AlertRule,
  CreateAlertRuleRequest,
  DeactivateAlertRuleRequest,
  DeleteAlertRuleRequest,
  ListAlertRulesRequest,
  UpdateAlertRuleRequest,
} from '@/api';
import { AlertRulesData } from '../models';

export interface AlertRulesSliceState {
  data: AlertRulesData;
  loading: boolean;
  error: string | null;
}

const initialState: AlertRulesSliceState = {
  data: null,
  loading: false,
  error: null,
};

const alertRulesSlice = createSlice({
  name: 'alertRules',
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<AlertRulesData>) => {
      state.data = action.payload;
    },
    addData: (state, action: PayloadAction<AlertRule>) => {
      if (state.data) {
        state.data.items = [action.payload, ...state.data.items];
        state.data.total += 1;
      }
    },
    removeData: (state, action: PayloadAction<number | string>) => {
      if (state.data) {
        const targetId = action.payload;
        state.data.items = state.data.items.filter(
          (item) => String(item.id) !== String(targetId),
        );
        state.data.total -= 1;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    createAlertRule: (
      state,
      action: PayloadAction<CreateAlertRuleRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    listAlertRules: (state, action: PayloadAction<ListAlertRulesRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    updateAlertRule: (
      state,
      action: PayloadAction<UpdateAlertRuleRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    deleteAlertRule: (
      state,
      action: PayloadAction<DeleteAlertRuleRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    activateAlertRule: (
      state,
      action: PayloadAction<ActivateAlertRuleRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    deactivateAlertRule: (
      state,
      action: PayloadAction<DeactivateAlertRuleRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    refreshAlertRule: (state, action: PayloadAction<AlertRule>) => {
      if (state.data?.items) {
        const currentItems: AlertRule[] = JSON.parse(
          JSON.stringify(state.data.items || []),
        );
        const newItems = [...currentItems];

        if (action.payload) {
          const payloadId = action.payload.id;
          const itemIndex = currentItems.findIndex(
            (item) => String(item.id) === String(payloadId),
          );

          if (itemIndex !== -1) {
            newItems.splice(itemIndex, 1, action.payload);
          }
        }

        state.data.items = newItems;
      }
    },
  },
});

export { alertRulesSlice };
